import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

export const MAIN_CONTENT_ID = "main-content";
export const ROUTE_FOCUS_SETTLE_MS = 3000;
export const USER_NAVIGATION_WINDOW_MS = 1000;

const HEADING_SELECTOR = "[data-page-heading]";
const PAGE_HEADING_SELECTOR = "h1[data-page-heading]";
const MODAL_SELECTOR = '[aria-modal="true"]';
const DROPDOWN_SELECTOR = ".dropdown";
const INPUT_EVENTS = ["pointerdown", "keydown", "click", "submit"];
const ACTIVATION_KEYS = new Set(["Enter", " "]);
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);
const FOCUS_OPTIONS = { preventScroll: true };

export const focusMainContent = (main, options = FOCUS_OPTIONS) => {
  if (!main.hasAttribute("tabindex")) {
    main.setAttribute("tabindex", "-1");
    main.addEventListener("blur", () => main.removeAttribute("tabindex"), {
      once: true,
    });
  }
  main.focus(options);
};

const isTextEntry = (element) => {
  if (!element || element.nodeType !== 1) return false;
  if (element.isContentEditable || element.tagName === "TEXTAREA") return true;
  return element.tagName === "INPUT" && !NON_TEXT_INPUT_TYPES.has(element.type);
};

const isActivation = (event) =>
  !isTextEntry(event.target) &&
  (event.type !== "keydown" || ACTIVATION_KEYS.has(event.key));

const isClosingDialog = (dialog) =>
  (dialog.classList.contains("modal") ||
    dialog.classList.contains("offcanvas")) &&
  !dialog.classList.contains("show");

const dialogState = (doc) => {
  const dialogs = Array.from(doc.querySelectorAll(MODAL_SELECTOR));
  if (dialogs.length === 0) return "none";
  return dialogs.every(isClosingDialog) ? "closing" : "open";
};

const pageHeadingIn = (main) =>
  main ? main.querySelector(PAGE_HEADING_SELECTOR) : null;

const readPageHeading = (doc) => {
  const heading = pageHeadingIn(doc.getElementById(MAIN_CONTENT_ID));
  return heading
    ? { element: new WeakRef(heading), text: heading.textContent }
    : null;
};

const readFocusedElement = (doc) =>
  doc.activeElement ? new WeakRef(doc.activeElement) : null;

const isSamePage = (before, heading) =>
  before !== null &&
  heading === before.element.deref() &&
  heading.textContent === before.text;

const focusTarget = (main, before, settled) => {
  const pageHeading = pageHeadingIn(main);
  if (pageHeading && !isSamePage(before, pageHeading)) return pageHeading;
  if (!pageHeading && !settled) return main;
  const headings = main.querySelectorAll(HEADING_SELECTOR);
  return headings.length > 0 ? headings[headings.length - 1] : main;
};

const focusIsLost = (doc) => {
  const active = doc.activeElement;
  return (
    !active ||
    active === doc.body ||
    active.id === MAIN_CONTENT_ID ||
    !active.isConnected
  );
};

const isTab = (element) => element.getAttribute("role") === "tab";

const holdsControl = (active, control) =>
  active === control || control.closest(DROPDOWN_SELECTOR)?.contains(active);

const linksTo = (control, pathname) =>
  control.getAttribute("href")?.startsWith("/") &&
  control.pathname === pathname;

const leavesFocus = (main, active, userStarted, control, pathname) => {
  if (control && !isTextEntry(control) && holdsControl(active, control)) {
    return isTab(control) || !(userStarted || linksTo(control, pathname));
  }
  return !userStarted || main.contains(active);
};

const afterRender = (win, callback) => {
  let timer = null;
  if (typeof win.requestAnimationFrame !== "function") {
    timer = win.setTimeout(callback, 0);
    return () => win.clearTimeout(timer);
  }
  const frame = win.requestAnimationFrame(() => {
    timer = win.setTimeout(callback, 0);
  });
  return () => {
    win.cancelAnimationFrame(frame);
    win.clearTimeout(timer);
  };
};

const startRouteFocus = (doc, userStarted, before, control, pathname) => {
  const win = doc.defaultView;
  let cancelPending = null;
  let deadline = null;
  let observer = null;
  let placed = null;
  let done = false;

  const stop = () => {
    done = true;
    cancelPending?.();
    cancelPending = null;
    observer?.disconnect();
    win.clearTimeout(deadline);
  };

  const attempt = () => {
    cancelPending = null;
    const main = doc.getElementById(MAIN_CONTENT_ID);
    const dialogs = dialogState(doc);
    if (!main || dialogs === "open") {
      stop();
      return;
    }
    if (dialogs === "closing") return;
    const active = doc.activeElement;
    if (placed) {
      const dropped = !placed.isConnected && (!active || active === doc.body);
      if (active !== placed && !dropped) {
        stop();
        return;
      }
    } else if (
      !focusIsLost(doc) &&
      leavesFocus(main, active, userStarted, control, pathname)
    ) {
      stop();
      return;
    }
    const target = focusTarget(main, before, false);
    if (target === main) {
      focusMainContent(main);
    } else if (target !== active) {
      target.focus(FOCUS_OPTIONS);
    }
    placed = target;
  };

  const schedule = () => {
    if (done || cancelPending) return;
    cancelPending = afterRender(win, attempt);
  };

  const settle = () => {
    if (
      placed?.id === MAIN_CONTENT_ID &&
      doc.activeElement === placed &&
      dialogState(doc) === "none"
    ) {
      const target = focusTarget(placed, before, true);
      if (target !== placed) target.focus(FOCUS_OPTIONS);
    }
    stop();
  };

  if (typeof MutationObserver !== "undefined") {
    observer = new MutationObserver(schedule);
    observer.observe(doc.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }
  deadline = win.setTimeout(settle, ROUTE_FOCUS_SETTLE_MS);
  schedule();
  return stop;
};

export function useRouteFocus() {
  const { pathname } = useLocation();
  const interacted = useRef(false);
  const lastActivation = useRef(null);
  const activatingControl = useRef(null);
  const pageHeading = useRef(null);

  useEffect(() => {
    const doc = document;
    const handleInput = (event) => {
      interacted.current = true;
      pageHeading.current = readPageHeading(doc);
      if (isActivation(event)) {
        lastActivation.current = Date.now();
        activatingControl.current = readFocusedElement(doc);
      }
    };
    INPUT_EVENTS.forEach((type) =>
      doc.addEventListener(type, handleInput, true),
    );
    return () =>
      INPUT_EVENTS.forEach((type) =>
        doc.removeEventListener(type, handleInput, true),
      );
  }, []);

  useEffect(() => {
    if (!interacted.current) return undefined;
    const userStarted =
      lastActivation.current !== null &&
      Date.now() - lastActivation.current <= USER_NAVIGATION_WINDOW_MS;
    return startRouteFocus(
      document,
      userStarted,
      pageHeading.current,
      activatingControl.current?.deref(),
      pathname,
    );
  }, [pathname]);
}
